import { Link, useLocation } from '@reach/router';
import React from 'react';
import clsx from 'clsx';
import NavItemIcon from './NavIcon';
import { useTheme } from '../../contexts/ThemeContext';

type Route = {
    path: string;
    name: string;
    parent?: boolean;
    children?: Route[];
};

function CloseIcon() {
    return (
        <svg
            className="w-5 h-5"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
        >
            <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M6 18L18 6M6 6l12 12"
            />
        </svg>
    );
}

function isPathActive(path: string, pathname: string) {
    return pathname === path || pathname.startsWith(`${path}/`);
}

function isGroupActive(route: Route, pathname: string) {
    if (isPathActive(route.path, pathname)) return true;
    return Boolean(route.children?.some((c) => isPathActive(c.path, pathname)));
}

// --- Mobile drawer: a single flat list with section headers for groups ---

function MobileNavItem({
    route,
    isChild,
    onNavigate,
}: {
    route: Route;
    isChild?: boolean;
    onNavigate?: () => void;
}) {
    const location = useLocation();
    const hasChildren = route.children && route.children.length > 0;
    const isActive = route.path === location.pathname;

    if (hasChildren) {
        return (
            <div className="mt-4">
                <div
                    className={clsx(
                        'px-4 mb-1 text-xs font-semibold uppercase tracking-wide text-foreground-muted',
                        { 'pl-8': isChild }
                    )}
                >
                    {route.name}
                </div>
                {route.children?.map((c) => (
                    <MobileNavItem route={c} key={c.name} isChild onNavigate={onNavigate} />
                ))}
            </div>
        );
    }

    return (
        <Link
            className={clsx(
                'flex items-center gap-3 h-10 mx-2 px-4 rounded-lg transition-colors',
                isChild && 'pl-8',
                isActive
                    ? 'bg-accent-soft text-orange-500'
                    : 'text-foreground-secondary hover:bg-surface-muted hover:text-foreground'
            )}
            to={route.path}
            onClick={onNavigate}
        >
            <span className="flex-shrink-0">
                <NavItemIcon route={route.name} />
            </span>
            <span className={clsx('text-sm', isActive ? 'font-semibold' : 'font-medium')}>
                {route.name}
            </span>
        </Link>
    );
}

// --- Desktop: a narrow icon rail for top-level sections, plus a secondary
// panel showing the active section's children (only when it has any). ---

function RailItem({ route, isActive }: { route: Route; isActive: boolean }) {
    const hasChildren = route.children && route.children.length > 0;
    const target = hasChildren ? (route.children as Route[])[0].path : route.path;

    return (
        <Link
            to={target}
            title={route.name}
            className={clsx(
                'flex flex-col items-center justify-center gap-1 w-full py-3 px-1 border-l-2 text-center transition-colors',
                isActive
                    ? 'border-orange-500 text-orange-500'
                    : 'border-transparent text-foreground-secondary hover:text-foreground hover:bg-surface-muted'
            )}
        >
            <NavItemIcon route={route.name} />
            <span className="text-xs font-medium leading-tight">{route.name}</span>
        </Link>
    );
}

function SecondaryPanel({ group }: { group: Route }) {
    const location = useLocation();

    return (
        <div className="hidden md:block w-56 bg-surface border-r border-border py-4 overflow-y-auto">
            <div className="px-4 mb-2 text-xs font-semibold uppercase tracking-wide text-foreground-muted">
                {group.name}
            </div>
            {group.children?.map((c) => {
                const isActive = isPathActive(c.path, location.pathname);
                return (
                    <Link
                        key={c.name}
                        to={c.path}
                        className={clsx(
                            'flex items-center h-9 mx-2 px-3 rounded-lg text-sm transition-colors',
                            isActive
                                ? 'bg-accent-soft text-orange-500 font-semibold'
                                : 'text-foreground-secondary hover:bg-surface-muted hover:text-foreground font-medium'
                        )}
                    >
                        {c.name}
                    </Link>
                );
            })}
        </div>
    );
}

export default function Sidebar({
    routes,
    isOpen,
    onClose,
    header,
}: {
    routes: Route[];
    isOpen?: boolean;
    onClose?: () => void;
    header?: React.ReactNode;
}) {
    const location = useLocation();
    const { theme } = useTheme();
    const logoSrc = theme === 'dark' ? '/logo-dark.png' : '/logo-light.png';
    const activeGroup = routes.find(
        (r) => r.children && r.children.length > 0 && isGroupActive(r, location.pathname)
    );

    return (
        <>
            {/* Mobile drawer */}
            {isOpen && (
                <div
                    className="fixed inset-0 z-30 bg-black bg-opacity-50 md:hidden"
                    onClick={onClose}
                    role="presentation"
                />
            )}
            <nav
                className={clsx(
                    'fixed inset-y-0 left-0 z-40 w-72 bg-surface border-r border-border overflow-y-auto transform transition-transform duration-200 ease-in-out md:hidden',
                    isOpen ? 'translate-x-0' : '-translate-x-full'
                )}
            >
                <div className="flex items-center justify-between h-14 px-4 border-b border-border">
                    <div className="flex items-center gap-2">
                        <img src={logoSrc} className="w-8 h-8" alt="" />
                        <span className="text-sm font-semibold text-foreground">Menu</span>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        aria-label="Close menu"
                        className="inline-flex items-center justify-center w-9 h-9 rounded-full text-foreground-secondary hover:bg-surface-muted hover:text-foreground transition-colors"
                    >
                        <CloseIcon />
                    </button>
                </div>
                {header && <div className="border-b border-border">{header}</div>}
                <div className="py-3">
                    {routes.map((route) => (
                        <MobileNavItem key={route.name} route={route} onNavigate={onClose} />
                    ))}
                </div>
            </nav>

            {/* Desktop rail + secondary panel */}
            <div className="hidden md:flex md:flex-shrink-0">
                <nav className="w-20 bg-surface border-r border-border flex flex-col items-center py-2 overflow-y-auto">
                    <div className="flex items-center justify-center h-14 mb-2">
                        <img src={logoSrc} className="w-8 h-8" alt="" />
                    </div>
                    {routes.map((route) => (
                        <RailItem
                            key={route.name}
                            route={route}
                            isActive={
                                route.children && route.children.length > 0
                                    ? activeGroup?.name === route.name
                                    : isPathActive(route.path, location.pathname)
                            }
                        />
                    ))}
                </nav>
                {activeGroup && <SecondaryPanel group={activeGroup} />}
            </div>
        </>
    );
}
